import { ref, computed, provide, type Ref, type ComputedRef, type DeepReadonly } from 'vue';
import {
  injectWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionValuesRecord,
  type InjectionKeysRecord
} from "@mcwd/util-vue";
import { isObject, type Nil, isNil } from '@mcwd/typescript-type-guards';
import type { MktoFormObj } from "../types/Marketo.js";
import { loadMarketoScript } from "../form-logic/loadMarketoScript.js";
import { useFormState } from "./use-form-state.js";
import { useFormSettings } from "./use-form-settings.js";
import { useFormEvents } from "./use-form-events.js";
import { useMarketoKnownVisitorState } from './use-marketo-known-visitor-status.js';
import { useFormLogger } from "./use-form-logger.js";

let formCounter = 0;

type UseMarketoInjectionValues = InjectionValuesRecord<{
  UniqInstanceId: number;
  MarketoFormObj: DeepReadonly<Ref<MktoFormObj | Nil>>;
  MarketoFormHtmlElement: ComputedRef<JQuery<HTMLFormElement> | Nil>;
  SubmitMarketoForm: () => void;
  LoadForm: () => Promise<void>;
  isMarketoFormValid: () => boolean | null;
}>;

type UseMarketoInjectionKeys = InjectionKeysRecord<UseMarketoInjectionValues>;

const UseMarketoInjectionKeys = CreateInjectionKeysRecord<UseMarketoInjectionKeys>({
  UniqInstanceId: "UniqInstanceId",
  MarketoFormObj: "MarketoFormObj",
  MarketoFormHtmlElement: "MarketoFormHtmlElement",
  SubmitMarketoForm: "SubmitMarketoForm",
  LoadForm: "LoadForm",
  isMarketoFormValid: "isMarketoFormValid"
});


export function initMarketoForm()
{
  if (checkValuesAlreadyProvided(UseMarketoInjectionKeys)) {
    return;
  }
  const { logger, getVerboseLogger } = useFormLogger();

  const verboseLogger = getVerboseLogger("initMarketoForm");
  verboseLogger.debug("initMarketoForm START");

  const uniqInstanceId = formCounter++;

  loadMarketoScript()
    .catch((err) => {
      logger.error(err);
      throw err;
    });

  const events = useFormEvents({ callerLabel: "initMarketoForm" });

  const { FormSettings } = useFormSettings({ callerLabel: "initMarketoForm" });
  const MarketoFormObj = ref<MktoFormObj | null>(null);
  provide(UseMarketoInjectionKeys.MarketoFormObj, MarketoFormObj);
  const MarketoFormHtmlElement = computed<JQuery<HTMLFormElement> | Nil>(() => {
    const mktoFormElemResult = MarketoFormObj.value?.getFormElem();
    if (isNil(mktoFormElemResult)) {
      return null;
    }
    else {
      const allFormElements = ([] as HTMLFormElement[]).slice.call(mktoFormElemResult);
      const filteredElems = allFormElements.filter(el => $(el).attr("data-uniq-instance-id")?.trim() === uniqInstanceId.toString());

      if (filteredElems.length === 0) {
        return null;
      }
      else if (filteredElems.length === 1) {
        return $<HTMLFormElement>(filteredElems[0]);
      }
      else {
        throw new Error(`Unexpected number of form elements with 'data-uniq-instance-id'='${uniqInstanceId}'`);
      }
    }
  });
  provide(UseMarketoInjectionKeys.MarketoFormHtmlElement, MarketoFormHtmlElement);

  const { initMarketoFormElement } = useMarketoKnownVisitorState();
  initMarketoFormElement(MarketoFormHtmlElement);

  const { setFormState } = useFormState({ callerLabel: "initMarketoForm" });

  async function setupFormMktoCallback(mktoFormObj: MktoFormObj) {
    verboseLogger.debug("setupFormMktoCallback START");
    // Initialize the MarketoFormObj ref
    MarketoFormObj.value = mktoFormObj;

    await setFormState("Setup");

    const formEl = mktoFormObj.getFormElem();
    formEl.removeAttr("id");

    // ---------------- Set handlers for marketo events ----------------

    mktoFormObj.onSuccess((/*_values, _followUpUrl*/) => {
      // events.completed.trigger is already called in setFormState;
      setFormState("Complete").catch((err) => logger.error(err));
      // Return false to prevent the submission handler from taking the lead to the follow up url
      return false;
    });

    mktoFormObj.onValidate((marketoFormValid: boolean) => {
      events.marketoValidation
        .trigger({
          eventName: "marketoValidation",
          marketoIsValid: marketoFormValid,
        })
        .catch((err) => logger.error(err));
    });
    mktoFormObj.onSubmit(() => {
      events.submit.trigger({ eventName: "submit" }).catch(err => logger.error(err));
    });

    await events.marketoSetup.trigger({ eventName: "marketoSetup" });

    // events.ready.trigger is already called in setFormState;
    await setFormState("Ready");
  }

  let loadFormPromise: Promise<void>;

  const LoadForm = (): Promise<void> => {
    if (isNil(loadFormPromise)) {
      loadFormPromise = new Promise((resolve, reject) => {
        verboseLogger.debug("LoadForm START");
        loadMarketoScript()
          .catch((err) => reject(err))
          .then(() => {
            return setFormState("Loading");
          }).then(() => {
            MktoForms2.loadForm(FormSettings.baseUrl, FormSettings.munchkinId, FormSettings.formId, (mktoFormObj) => {
              setupFormMktoCallback(mktoFormObj).then(() => {
                verboseLogger.debug("LoadForm END");
                resolve();
              }).catch(err => {
                reject(err);
              });
            });
          }).catch((err) => reject(err));
      });
    }
    return loadFormPromise;
  };

  const SubmitMarketoForm = () => {
    const formEl = MarketoFormObj.value?.getFormElem();
    if (isObject(formEl)) {
      const mktoSubmitButton = formEl.find(".mktoButton[type='submit']");
      if (mktoSubmitButton.length > 0) {
        if (mktoSubmitButton.length > 1) {
          logger.error("Unexpected number of .mktoButton[type='submit']");
        }
        else {
          mktoSubmitButton.trigger("click");
        }
      }
    }
  };

  const checkMarketoFormValid = (): boolean | null => {
    if (MarketoFormObj.value === null) {
      return null;
    }
    return MarketoFormObj.value.submittable();
  };
  
  provide(UseMarketoInjectionKeys.LoadForm, LoadForm);
  provide(UseMarketoInjectionKeys.SubmitMarketoForm, SubmitMarketoForm);
  provide(UseMarketoInjectionKeys.isMarketoFormValid, checkMarketoFormValid);
  provide(UseMarketoInjectionKeys.UniqInstanceId, uniqInstanceId);
}

function getNilObjPropNames(obj: Record<string, unknown>) {
  return Object.keys(obj).filter(k => isNil(obj[k]));
}

function injectOrError<TKeyToInject extends (keyof UseMarketoInjectionKeys)>(keysToInject: TKeyToInject[]): { [key in TKeyToInject]: UseMarketoInjectionValues[key] } {  
  type TReturn = { [key in TKeyToInject]: UseMarketoInjectionValues[key] };

  const injectedValues: Partial<TReturn> = {};

  for (const key of keysToInject) {
    injectedValues[key] = injectWithSelf(UseMarketoInjectionKeys[key]);
  }
  const nilVariableNames = getNilObjPropNames(injectedValues);

  if (nilVariableNames.length > 0) {
    throw new Error(`Form has not been initialized.  Undefined variables: ${nilVariableNames.toString()}`);
  }

  return injectedValues as TReturn;
}

export const useMarketoForm = ({ callerLabel }: { callerLabel?: string | undefined } = {}) => {
  const { logger } = useFormLogger({ callerLabel: "useMarketoForm" });
  try {
    return injectOrError([
      "LoadForm",
      "UniqInstanceId",
    ]);
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
};

export const useMarketoFormInternals = ({ callerLabel }: { callerLabel?: string | undefined } = {}) => {
  const { logger } = useFormLogger({ callerLabel: "useMarketoFormInternals" });
  try {
    return injectOrError([
      "MarketoFormObj",
      "MarketoFormHtmlElement",
      "SubmitMarketoForm",
      "UniqInstanceId",
    ]);
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
};